import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Search, X, Share2, Plus, Minus, ArrowUp, Grid, Eye, Check, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_API_URL || "https://api.shopkwality.in";

const Home = () => {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Product details modal
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const observerRef = useRef(null);
  const bottomRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const handleAddCustomFromSearch = (name) => {
    if (!name.trim()) return;
    const stored = JSON.parse(localStorage.getItem("cart") || "[]");

    // Check if custom product with same name already exists in cart
    const existing = stored.find(p => p.is_custom && p.name.toLowerCase() === name.trim().toLowerCase());
    let updated;

    if (existing) {
      existing.quantity += 1;
      updated = [...stored];
    } else {
      const newItem = {
        id: `custom_${Date.now()}`,
        name: name.trim(),
        product_desc: `Requested customly from search catalog query: "${name.trim()}"`,
        is_custom: true,
        quantity: 1
      };
      updated = [...stored, newItem];
    }

    setCart(updated);
    localStorage.setItem("cart", JSON.stringify(updated));
    window.dispatchEvent(new Event("cart-updated"));

    setToastMessage(`Added custom product "${name.trim()}" to your quote cart!`);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  /* ---------------- FETCH PRODUCTS ---------------- */

  const fetchProducts = async (reset = false, pageNum = 1) => {
    if (loading) return;
    setLoading(true);

    try {
      const params = {
        page: pageNum,
        limit: 12,
      };

      if (search && search.trim()) {
        params.search = search.trim();
      }

      if (selectedCategory) {
        params.categoryId = selectedCategory;
      }

      const res = await axios.get(`${API}/products/public`, { params });
      const { data, meta } = res.data;

      setPage(meta.page);
      setTotalPages(meta.totalPages);

      if (reset) {
        setProducts(data);
        if (pageNum === 1) {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      } else {
        setProducts(prev => [...prev, ...data]);
      }
    } catch (err) {
      console.error("Failed to fetch products", err);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- INITIAL LOAD ---------------- */

  useEffect(() => {
    axios.get(`${API}/categories`)
      .then(res => setCategories(res.data))
      .catch(console.error);

    const stored = JSON.parse(localStorage.getItem("cart") || "[]");
    setCart(stored);

    fetchProducts(true, 1);

    // Handle deep-linked product share URLs: /?product=ID
    const params = new URLSearchParams(window.location.search);
    const sharedProductId = params.get("product");
    if (sharedProductId) {
      axios.get(`${API}/products/public/${sharedProductId}`)
        .then(res => {
          if (res.data) setSelectedProduct(res.data);
        })
        .catch(err => console.warn("Shared product not found:", err));
    }
  }, []);

  /* ---------------- DEBOUNCED SEARCH / FILTER ---------------- */

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      fetchProducts(true, 1);
    }, 400);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search, selectedCategory]);

  /* ---------------- INFINITE SCROLL ---------------- */

  useEffect(() => {
    if (!bottomRef.current) return;

    observerRef.current = new IntersectionObserver(
      entries => {
        if (
          entries[0].isIntersecting &&
          page < totalPages &&
          !loading
        ) {
          fetchProducts(false, page + 1);
        }
      },
      { threshold: 0.2 }
    );

    observerRef.current.observe(bottomRef.current);

    return () => observerRef.current?.disconnect();
  }, [page, totalPages, loading]);

  /* ---------------- BACK TO TOP VISIBILITY ---------------- */

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 450);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* ---------------- CART ACTIONS ---------------- */

  const updateCart = (product, delta) => {
    const updated = [...cart];
    const existing = updated.find(p => p.id === product.id);

    if (existing) {
      existing.quantity += delta;
      if (existing.quantity <= 0) {
        updated.splice(updated.indexOf(existing), 1);
      }
    } else if (delta > 0) {
      updated.push({ ...product, quantity: 1 });
    }

    setCart(updated);
    localStorage.setItem("cart", JSON.stringify(updated));
    window.dispatchEvent(new Event("cart-updated"));
  };

  const handleQtyChange = (product, val) => {
    const updated = [...cart];
    const existing = updated.find(p => p.id === product.id);
    if (!existing) return;

    if (val === "") {
      existing.quantity = "";
      setCart(updated);
      localStorage.setItem("cart", JSON.stringify(updated.map(p => p.quantity === "" ? { ...p, quantity: 1 } : p)));
      window.dispatchEvent(new Event("cart-updated"));
      return;
    }

    const parsed = parseInt(val, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      existing.quantity = parsed;
      let finalUpdated = updated;
      if (parsed === 0) {
        finalUpdated = updated.filter(p => p.id !== product.id);
      }
      setCart(finalUpdated);
      localStorage.setItem("cart", JSON.stringify(finalUpdated));
      window.dispatchEvent(new Event("cart-updated"));
    }
  };

  const handleQtyBlur = (product, val) => {
    const parsed = parseInt(val, 10);
    if (isNaN(parsed) || parsed <= 0) {
      const updated = [...cart];
      const existing = updated.find(p => p.id === product.id);
      if (existing) {
        existing.quantity = 1;
        setCart(updated);
        localStorage.setItem("cart", JSON.stringify(updated));
        window.dispatchEvent(new Event("cart-updated"));
      }
    }
  };

  /* ---------------- SHARE ACTION ---------------- */

  const handleShareProduct = (product, e) => {
    e.stopPropagation();

    // Generate a shareable link to this product on the public catalog
    const base = window.location.origin;
    const shareUrl = `${base}/?product=${product.id}&name=${encodeURIComponent(product.name)}`;

    if (navigator.share) {
      // Use native share sheet on mobile
      navigator.share({
        title: `KTZ — ${product.name}`,
        text: `Check out ${product.name} on Kwality Tech Zone`,
        url: shareUrl
      }).catch(() => { });
    } else {
      navigator.clipboard.writeText(shareUrl)
        .then(() => {
          setCopiedId(product.id);
          setTimeout(() => setCopiedId(null), 2500);
        })
        .catch(err => console.error("Clipboard error:", err));
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 select-none pb-12">
      <div className="max-w-lg mx-auto px-4 py-8">

        {/* Minimal Hero Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-2">
            Electronics Catalog
          </h1>
          <p className="text-sm text-slate-500 font-medium">
            Explore our state-of-the-art catalog of premium components and customized electronics solutions.
          </p>
        </div>

        {/* Search & Filter - Sticky on Scroll */}
        <div className="mb-8 space-y-3 sticky top-[4.1rem] z-40 bg-slate-50/95 backdrop-blur-md py-4 transition-all duration-200">

          {/* Unified search container */}
          <div className="relative group">
            <Search className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search components..."
              className="w-full border border-slate-200 rounded-2xl pl-12 pr-12 py-3.5 
                focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50
                transition-all duration-300 bg-white shadow-sm font-medium text-slate-700 text-sm"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 
                  hover:text-slate-600 transition-colors p-1 hover:bg-slate-100 rounded-full"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Elegant selector */}
          <div className="relative">
            <select
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              className="w-full border border-slate-200 rounded-2xl px-4 py-3.5 
                focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50
                transition-all duration-300 bg-white shadow-sm font-semibold text-slate-600 text-xs tracking-wider uppercase
                appearance-none bg-no-repeat bg-right cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b' stroke-width='2.5'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2.5' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundSize: "1rem",
                backgroundPosition: "right 1rem center"
              }}
            >
              <option value="">All Categories</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Quick Custom Product Adder Banner */}
        {search && search.trim() && (
          <div className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-100 rounded-3xl p-4 shadow-sm flex items-center justify-between gap-4 animate-slide-up">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-2xl flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 animate-pulse" />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-xs font-extrabold text-slate-800 leading-tight">Can't find '{search}'?</h4>
                <p className="text-[10px] text-slate-400 font-bold leading-tight mt-0.5">
                  Request it directly as a catalog-independent custom product.
                </p>
              </div>
            </div>
            <button
              onClick={() => handleAddCustomFromSearch(search)}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold text-[10px] uppercase tracking-wider py-2.5 px-4 rounded-xl transition-all duration-200 active:scale-95 shrink-0 flex items-center gap-1 shadow-md"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add Custom</span>
            </button>
          </div>
        )}

        {/* Products Grid */}
        <div className="grid grid-cols-2 gap-4">
          {products.length === 0 && !loading && (
            <div className="col-span-2 text-center py-16 px-4 bg-white rounded-3xl border border-slate-200 shadow-sm space-y-5">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <Grid className="w-8 h-8 text-slate-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">No components found</h3>
                <p className="text-sm text-slate-400 mt-1 max-w-[15rem] mx-auto">
                  We couldn't find '{search}' in our catalog. Try requesting it customly!
                </p>
              </div>
              {search && search.trim() && (
                <button
                  onClick={() => handleAddCustomFromSearch(search)}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs uppercase tracking-widest py-3.5 px-6 rounded-2xl transition-all duration-200 active:scale-95 shadow-md flex items-center gap-2 mx-auto"
                >
                  <Sparkles className="w-4 h-4 animate-pulse" />
                  <span>Request "{search}" Customly</span>
                </button>
              )}
            </div>
          )}

          {products.map(product => {
            const inCart = cart.find(p => p.id === product.id);

            return (
              <div
                key={product.id}
                onClick={() => setSelectedProduct(product)}
                className="group border border-slate-200 rounded-3xl p-3 bg-white shadow-sm 
                  hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 cursor-pointer flex flex-col justify-between relative overflow-hidden"
              >
                <div>
                  {/* Image panel (Dominant!) */}
                  <div className="relative aspect-square w-full bg-slate-50 border border-slate-100
                    rounded-2xl overflow-hidden mb-3 flex items-center justify-center">
                    <img
                      src={`${API}/uploads/${product.image || "default.png"}`}
                      alt={product.name}
                      className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=200&auto=format&fit=crop&q=60" }}
                    />
                  </div>

                  {/* Details layout */}
                  <h2 className="font-bold text-slate-800 text-[11px] tracking-tight leading-snug line-clamp-2 mb-1 min-h-[1.75rem]">
                    {product.name}
                  </h2>
                </div>

                {/* Actions overlay */}
                <div className="flex items-center gap-2 mt-auto">
                  {/* Share Action */}
                  <button
                    onClick={(e) => handleShareProduct(product, e)}
                    className={`h-9 w-9 flex items-center justify-center rounded-xl border transition-all duration-200 shrink-0 ${copiedId === product.id
                        ? "bg-green-50 border-green-200 text-green-600"
                        : "bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-500 hover:text-slate-700"
                      }`}
                    title="Share Product Card"
                  >
                    {copiedId === product.id ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
                  </button>

                  {/* Quantity controls */}
                  <div className="flex-1 min-w-0" onClick={e => e.stopPropagation()}>
                    {inCart ? (
                      <div className="flex items-center justify-between bg-blue-50/70 border border-blue-100 rounded-xl px-1 py-1">
                        <button
                          onClick={() => updateCart(product, -1)}
                          className="w-6.5 h-6.5 flex items-center justify-center rounded-lg 
                            bg-white text-red-600 shadow-sm active:scale-90 font-bold"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <input
                          type="number"
                          value={inCart.quantity}
                          min="1"
                          onChange={e => handleQtyChange(product, e.target.value)}
                          onBlur={e => handleQtyBlur(product, e.target.value)}
                          className="font-bold text-blue-900 text-xs w-8 text-center bg-transparent border-none focus:outline-none focus:ring-0 p-0 m-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <button
                          onClick={() => updateCart(product, 1)}
                          className="w-6.5 h-6.5 flex items-center justify-center rounded-lg 
                            bg-white text-green-600 shadow-sm active:scale-90 font-bold"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => updateCart(product, 1)}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold uppercase tracking-wider
                          py-2.5 rounded-xl w-full transition-all duration-200 active:scale-95 shadow-sm text-center"
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>

                {/* Copied visual notification toast */}
                {copiedId === product.id && (
                  <div className="absolute top-2 right-2 bg-green-600 text-white text-[8px] font-bold px-1.5 py-0.5 rounded shadow-sm z-10">
                    Copied!
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Loading Sentinel */}
        <div ref={bottomRef} className="h-16 flex items-center justify-center mt-6">
          {loading && (
            <div className="flex items-center gap-2.5 text-slate-500">
              <svg className="animate-spin h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-xs font-semibold tracking-wider text-slate-400 uppercase">Loading more...</span>
            </div>
          )}
          {!loading && page >= totalPages && products.length > 0 && (
            <p className="text-slate-400 text-xs font-semibold tracking-widest uppercase">Catalog Complete</p>
          )}
        </div>
      </div>

      {/* Back to Top Button - Fixed Position */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-6 right-6 w-12 h-12 bg-slate-900 text-white rounded-2xl shadow-xl hover:shadow-2xl
          transition-all duration-300 z-50 flex items-center justify-center
          hover:scale-105 active:scale-95 group border border-slate-800
          ${showBackToTop ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0 pointer-events-none"}`}
        aria-label="Back to top"
      >
        <ArrowUp className="w-5 h-5 transform group-hover:-translate-y-0.5 transition-transform" />
      </button>

      {/* Product Details Drawer Overlay */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-end justify-center" onClick={() => setSelectedProduct(null)}>
          <div
            className="w-full max-w-lg bg-white rounded-t-[2.5rem] p-6 shadow-2xl transition-all duration-300 animate-slide-up select-none max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Header notch */}
            <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-6" onClick={() => setSelectedProduct(null)} />

            {/* Detail Content */}
            <div className="space-y-6">
              {/* Product Big Image */}
              <div className="w-full h-48 bg-slate-50 border border-slate-100 rounded-3xl flex items-center justify-center p-4 relative">
                <img
                  src={`${API}/uploads/${selectedProduct.image || "default.png"}`}
                  alt={selectedProduct.name}
                  className="w-full h-full object-contain"
                  onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=400&auto=format&fit=crop&q=80" }}
                />
              </div>

              {/* Product Info */}
              <div>
                <h2 className="text-xl font-extrabold text-slate-800 tracking-tight leading-snug mb-2">
                  {selectedProduct.name}
                </h2>
                <div className="flex flex-wrap gap-2 mb-4">
                  {categories.find(c => String(c.id) === String(selectedProduct.category_id)) && (
                    <span className="text-xs font-bold text-blue-600 tracking-wider bg-blue-50 px-3 py-1 rounded-xl">
                      Category: {categories.find(c => String(c.id) === String(selectedProduct.category_id))?.name}
                    </span>
                  )}
                </div>

                <div className="border-t border-slate-100 pt-4">
                  <h4 className="text-xs font-extrabold text-slate-400 tracking-widest uppercase mb-2">Description</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {selectedProduct.description || "No description provided for this catalog component."}
                  </p>
                </div>
              </div>

              {/* Cart addition pane inside drawer */}
              <div className="flex gap-4 border-t border-slate-100 pt-6">
                <button
                  onClick={(e) => {
                    handleShareProduct(selectedProduct, e);
                  }}
                  className={`flex-1 py-3.5 border rounded-2xl font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all duration-200 ${copiedId === selectedProduct.id
                      ? "bg-green-50 border-green-200 text-green-600"
                      : "bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-600"
                    }`}
                >
                  <Share2 className="w-4 h-4" />
                  <span>{copiedId === selectedProduct.id ? "Link Copied!" : "Share Link"}</span>
                </button>

                <div className="flex-1 flex" onClick={e => e.stopPropagation()}>
                  {cart.find(p => p.id === selectedProduct.id) ? (
                    <div className="flex items-center justify-between bg-blue-50/70 border border-blue-100 rounded-2xl px-3 w-full py-1">
                      <button
                        onClick={() => updateCart(selectedProduct, -1)}
                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-white text-red-600 shadow-sm active:scale-90"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <input
                        type="number"
                        value={cart.find(p => p.id === selectedProduct.id).quantity}
                        min="1"
                        onChange={e => handleQtyChange(selectedProduct, e.target.value)}
                        onBlur={e => handleQtyBlur(selectedProduct, e.target.value)}
                        className="font-extrabold text-blue-900 text-base w-12 text-center bg-transparent border-none focus:outline-none focus:ring-0 p-0 m-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <button
                        onClick={() => updateCart(selectedProduct, 1)}
                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-white text-green-600 shadow-sm active:scale-90"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => updateCart(selectedProduct, 1)}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase tracking-widest
                        py-4 rounded-2xl w-full transition-all duration-200 active:scale-95 shadow-md flex items-center justify-center gap-2"
                    >
                      Add to Quotation
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Dynamic Toast Feedback */}
      {showToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900/95 backdrop-blur text-white text-xs font-bold px-5 py-4 rounded-2xl shadow-2xl z-50 flex items-center gap-2.5 border border-slate-800 animate-slide-up max-w-[90vw] text-center">
          <Sparkles className="w-4 h-4 text-purple-400 animate-pulse shrink-0" />
          <span className="truncate">{toastMessage}</span>
        </div>
      )}
    </div>
  );
};

export default Home;